/**
 * Given a polygon definition with an outline and holes specified separately, return
 * a single polygon with zero-width bridges to the holes, so the entire shape can be
 * specified as an outline, for situations that only support simple outlines, such as
 * Gerber/KiCad.
 *
 * Note: polygon must be non-self-intersecting, holes must be non-overlapping, and holes
 * must be entirely contained within the outline. If this isn't the case for your shape,
 * use ClipperJS (eg union and difference operations) to make it so.
 *
 * Islands within holes are not supported. (However, for KiCad/Gerber support, these are
 * easily handled as separate polygons in the caller).
 *
 * Based on KiCad's fractureSingle() algorithm defined here:
 * https://gitlab.com/kicad/code/kicad/-/blob/master/libs/kimath/src/geometry/shape_poly_set.cpp#L963
 *
 * Note: poly-partition.removeHoles() does a similar thing. However it is *very* slow (O(n^3)-ish,
 * looks like). Use this instead.
 */
export function removePolyHoles(outline, holes) {
    // It's important for this algorithm that hole orientation is the reverse of
    // outline orientation, otherwise we get intersecting bridges. The caller
    // doesn't always guarantee this.
    outline = isClockWise(outline) ? outline : [...outline].reverse();
    holes = holes.map((h) => (isClockWise(h) ? [...h].reverse() : h));
    const holesByLeftVertex = sortedPolygonsByLeftVertex(holes);
    outline = outline.slice();
    for (const [leftIdx, hole] of holesByLeftVertex) {
        const holeVertex = hole[leftIdx];
        const intersect = getLeftIntersect(outline, holeVertex);
        if (!intersect) {
            throw new Error("No outline intersect found! This should never happen, and probably means you passed in an incomplete outline or overlapping holes.");
        }
        const intersectPoint = intersect[1];
        // Now splice the hole into the outline shape at the given intersect point, with a bridge:
        outline.splice(intersect[0][1], 0, intersectPoint, ...hole.slice(leftIdx), ...hole.slice(0, leftIdx), holeVertex, intersectPoint);
    }
    return outline;
}
/**
 * Given a set of hole polygons, return them sorted by left side,
 * left-to-right, paired with the index of each polygon's leftmost vertex.
 */
function sortedPolygonsByLeftVertex(polys) {
    const holesByLeftVertex = polys.map((h) => [leftMostVertexIndex(h), h]);
    holesByLeftVertex.sort((a, b) => a[1][a[0]].x - b[1][b[0]].x);
    return holesByLeftVertex;
}
/**
 * Given an outline polygon, and a vertex inside that outline, return the
 * closest point to the left of the vertex where a horizontal line from the
 * vertex would intersect the outline, along with the indices of the intersected
 * edge's endpoints.
 */
function getLeftIntersect(outline, holeVertex) {
    let intersect;
    // Yes, we really do want a `for i` loop below, because we use the indices.
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let idx = 0; idx < outline.length; idx++) {
        const edgeIndices = [idx, (idx + 1) % outline.length];
        const edge = [outline[edgeIndices[0]], outline[edgeIndices[1]]];
        if (!hasHorizontalIntersect(holeVertex, edge))
            continue;
        // Don't consider horizontal edges to intersect; rely on the fact
        // that they always connect to a non-horizontal edge at the same
        // y coordinate; keeps the algorithm simpler.
        if (edge[0].y === edge[1].y) {
            continue;
        }
        const intersectPoint = getIntersectPoint(edge, holeVertex);
        if (intersectPoint.x > holeVertex.x)
            continue; // Skip intersects to the right.
        if (!intersect || holeVertex.x - intersectPoint.x < holeVertex.x - intersect[1].x) {
            intersect = [edgeIndices, intersectPoint];
        }
    }
    return intersect;
}
function hasHorizontalIntersect(holeVertex, edge) {
    const edgeYRange = [edge[0].y, edge[1].y].sort((a, b) => a - b); // Ensure increasing order.
    // NOTE: we have seen floating point differences here like between
    // 6.82939016500001 - 6.829390165
    // TODO we should use compareFloats() here. But test carefully if you change it!
    const floatingPointTolerance = 0.0000000001;
    return (holeVertex.y >= edgeYRange[0] - floatingPointTolerance &&
        holeVertex.y <= edgeYRange[1] + floatingPointTolerance);
}
function getIntersectPoint(edge, holeVertex) {
    if (edge[0].x === edge[1].x) {
        return { x: edge[0].x, y: holeVertex.y };
    }
    const slope = (edge[1].y - edge[0].y) / (edge[1].x - edge[0].x);
    const intersectX = edge[0].x + (holeVertex.y - edge[0].y) / slope;
    return { x: intersectX, y: holeVertex.y };
}
function leftMostVertexIndex(poly) {
    return poly.reduce((leftMost, v, idx) => (v.x < poly[leftMost].x ? idx : leftMost), 0);
}
function signedArea(contour) {
    // Taken from https://github.com/mrdoob/three.js/blob/master/src/extras/ShapeUtils.js
    const n = contour.length;
    let a = 0.0;
    for (let p = n - 1, q = 0; q < n; p = q++) {
        a += contour[p].x * contour[q].y - contour[q].x * contour[p].y;
    }
    return a * 0.5;
}
function isClockWise(contour) {
    // Taken from https://github.com/mrdoob/three.js/blob/master/src/extras/ShapeUtils.js
    return signedArea(contour) < 0;
}
//# sourceMappingURL=removePolyHoles.js.map